import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Download, RefreshCw, Loader2, FileText, Image as ImageIcon, Headphones, BarChart3, Eye, ArrowLeft, ChevronRight, Globe, Shield, TrendingUp, AlertTriangle, Building, Users, Landmark, Scale, Heart, BookOpen, Calendar, Presentation, ExternalLink, X, Palette, SlidersHorizontal, Type, Languages, Layers } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CountryTrendView from '../components/CountryTrendView';
import SectionSourcesPanel from '../components/SectionSourcesPanel';
import SectionLiveDataPanel from '../components/SectionLiveDataPanel';
import SectionQualityPanel from '../components/SectionQualityPanel';
import BlueprintReferencesPanel from '../components/BlueprintReferencesPanel';
import StakeholderPowerInterestGrid from '../components/StakeholderPowerInterestGrid';
import PortfolioBenchmarkHeatmap from '../components/PortfolioBenchmarkHeatmap';
import SectionChatBox from '../components/SectionChatBox';
import SectionObservationsPanel from '../components/SectionObservationsPanel';
import FullBlueprintPipelinePanel from '../components/FullBlueprintPipelinePanel';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getErrorMessage = (error) => {
  if (error.response?.data?.detail) return error.response.data.detail;
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout'))
    return 'Request timed out. The AI service may be busy — please try again in a few minutes.';
  if (error.message?.includes('Network Error'))
    return 'Network error. Please check your connection and try again.';
  if (error.response?.status === 503)
    return 'AI service is temporarily unavailable. Please try again in a few minutes.';
  if (error.response?.status === 500)
    return 'Server error. Please try again.';
  return 'Something went wrong. Please try again.';
};

const SECTIONS = [
  // SCOUT (1-12)
  { key: 'country_snapshot', title: 'Country Snapshot', icon: Globe, shortTitle: 'Snapshot', tier: 'scout', num: 1 },
  { key: 'executive_summary', title: 'Executive Summary', icon: BarChart3, shortTitle: 'Executive', tier: 'scout', num: 2 },
  { key: 'pestle_analysis', title: 'PESTLE Analysis', icon: Scale, shortTitle: 'PESTLE', tier: 'scout', num: 3 },
  { key: 'market_opportunity', title: 'Market Opportunity', icon: TrendingUp, shortTitle: 'Market', tier: 'scout', num: 4 },
  { key: 'government_capacity', title: 'Government Capacity', icon: Landmark, shortTitle: 'Government', tier: 'scout', num: 5 },
  { key: 'competitive_landscape', title: 'Competitive Landscape', icon: Building, shortTitle: 'Competition', tier: 'scout', num: 6 },
  { key: 'business_model', title: 'Business Model', icon: FileText, shortTitle: 'Business', tier: 'scout', num: 7 },
  { key: 'risk_analysis', title: 'Risk Analysis', icon: AlertTriangle, shortTitle: 'Risk', tier: 'scout', num: 8 },
  { key: 'financial_projections', title: 'Financial Projections', icon: TrendingUp, shortTitle: 'Financials', tier: 'scout', num: 9 },
  { key: 'sovereign_risk', title: 'Sovereign Risk', icon: Shield, shortTitle: 'Sovereign', tier: 'scout', num: 10 },
  { key: 'social_impact', title: 'Social Impact & ESG', icon: Heart, shortTitle: 'Impact', tier: 'scout', num: 11 },
  { key: 'governance', title: 'Governance & PPP', icon: BookOpen, shortTitle: 'Governance', tier: 'scout', num: 12 },
  // STRATEGIST (13-22)
  { key: 'stakeholder_map', title: 'Stakeholder Map', icon: Users, shortTitle: 'Stakeholders', tier: 'strategist', num: 13 },
  { key: 'regulatory_deep_dive', title: 'Regulatory Deep Dive', icon: Scale, shortTitle: 'Regulatory', tier: 'strategist', num: 14 },
  { key: 'value_chain', title: 'Value Chain & Margins', icon: Layers, shortTitle: 'Value Chain', tier: 'strategist', num: 15 },
  { key: 'tech_readiness', title: 'Technology Readiness', icon: Globe, shortTitle: 'Technology', tier: 'strategist', num: 16 },
  { key: 'entry_strategy', title: 'Entry Strategy', icon: TrendingUp, shortTitle: 'Entry', tier: 'strategist', num: 17 },
  { key: 'local_partners', title: 'Local Partner & JV', icon: Users, shortTitle: 'Partners', tier: 'strategist', num: 18 },
  { key: 'scenario_planning', title: 'Scenario Planning', icon: AlertTriangle, shortTitle: 'Scenarios', tier: 'strategist', num: 19 },
  { key: 'tax_incentives', title: 'Tax & Incentive Guide', icon: FileText, shortTitle: 'Tax Guide', tier: 'strategist', num: 20 },
  { key: 'talent_labor', title: 'Talent & Labor', icon: Users, shortTitle: 'Talent', tier: 'strategist', num: 21 },
  { key: 'supply_chain', title: 'Supply Chain', icon: Building, shortTitle: 'Logistics', tier: 'strategist', num: 22 },
  // SOVEREIGN (23-30)
  { key: 'dfi_landscape', title: 'DFI & Blended Finance', icon: Landmark, shortTitle: 'DFI Finance', tier: 'sovereign', num: 23 },
  { key: 'deal_structuring', title: 'Deal Structuring', icon: FileText, shortTitle: 'Deal PPP', tier: 'sovereign', num: 24 },
  { key: 'ma_targets', title: 'M&A Targets', icon: TrendingUp, shortTitle: 'M&A', tier: 'sovereign', num: 25 },
  { key: 'geopolitical_brief', title: 'Geopolitical Brief', icon: Shield, shortTitle: 'Geopolitical', tier: 'sovereign', num: 26 },
  { key: 'diplomatic_tracker', title: 'Diplomatic Relations', icon: Globe, shortTitle: 'Diplomatic', tier: 'sovereign', num: 27 },
  { key: 'investment_memo', title: 'IC Investment Memo', icon: FileText, shortTitle: 'IC Memo', tier: 'sovereign', num: 28 },
  { key: 'portfolio_benchmark', title: 'Portfolio Benchmark', icon: BarChart3, shortTitle: 'Benchmark', tier: 'sovereign', num: 29 },
  { key: 'execution_playbook', title: '100-Day Playbook', icon: ChevronRight, shortTitle: '100-Day', tier: 'sovereign', num: 30 },
  { key: 'capital_markets', title: 'Capital Markets & Listing Pathways', icon: TrendingUp, shortTitle: 'Capital Markets', tier: 'sovereign', num: 31 },
];

const TIER_INFO = {
  scout: { name: 'SCOUT', color: '#004ac6', bg: '#e5eeff' },
  strategist: { name: 'STRATEGIST', color: '#00A651', bg: '#ecfdf5' },
  sovereign: { name: 'SOVEREIGN', color: '#7c3aed', bg: '#f3f0ff' },
};

const SECTION_SENTINEL = '--- END OF SECTION ---';
const preprocessMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text;
  // Strip the completeness sentinel so it never appears in the rendered UI
  text = text.replace(/\n*---\s*END OF SECTION\s*---\s*$/i, '').trimEnd();
  const lines = text.split('\n');
  const result = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const nextLine = lines[i + 1] || '';
    result.push(line);
    const isTableLine = line.trim().startsWith('|');
    const isNextTableLine = nextLine.trim().startsWith('|');
    const isBlank = line.trim() === '';
    const isNextBlank = nextLine.trim() === '';
    const isHeading = line.trim().startsWith('#');
    const isNextHeading = nextLine.trim().startsWith('#');
    const isList = /^[\s]*[-*\d]+[.)]\s/.test(line);
    const isNextList = /^[\s]*[-*\d]+[.)]\s/.test(nextLine);
    if (!isBlank && !isNextBlank && !isTableLine && !isNextTableLine &&
        line.trim().length > 0 && nextLine.trim().length > 0 &&
        !isHeading && !isNextHeading && !isList && !isNextList) {
      if ((line.trim().endsWith('.') || line.trim().endsWith(':')) && nextLine.trim().match(/^[A-Z]/)) {
        result.push('');
      }
    }
  }
  return result.join('\n');
};

function BlueprintViewPage() {
  const { countryId } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('executive_summary');
  const [generatingSections, setGeneratingSections] = useState({});
  const [generatingIllustrations, setGeneratingIllustrations] = useState({});
  const [generatingReferences, setGeneratingReferences] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [generatingInfographic, setGeneratingInfographic] = useState(false);
  const [generatingPresentation, setGeneratingPresentation] = useState(false);
  const [presentationUrl, setPresentationUrl] = useState(null);
  const [showGammaModal, setShowGammaModal] = useState(false);
  const [gammaThemes, setGammaThemes] = useState([]);
  const [gammaOptions, setGammaOptions] = useState({ themeId: '', numCards: 14, textMode: 'generate', language: '' });
  const [loadingThemes, setLoadingThemes] = useState(false);
  const [reportVersions, setReportVersions] = useState([]);
  const [contentHash, setContentHash] = useState('');
  const [completeness, setCompleteness] = useState(null);
  const sectionRefs = useRef({});

  useEffect(() => { fetchCountry(); }, [countryId]);

  // Refresh completeness check whenever sections change
  useEffect(() => {
    if (!country?.sections_generated?.length) { setCompleteness(null); return; }
    axios.get(`${API}/countries/${countryId}/sections-completeness`)
      .then(r => setCompleteness(r.data))
      .catch(() => setCompleteness(null));
  }, [country?.sections_generated?.length, country?.updated_at, countryId]);

  const fetchCountry = async () => {
    try {
      
      const response = await axios.get(`${API}/countries/${countryId}`, {
        
      });
      setCountry(response.data);
      if (response.data.gamma_url) setPresentationUrl(response.data.gamma_url);
      // Load report versions
      try {
        const vResp = await axios.get(`${API}/countries/${countryId}/report-versions`);
        setReportVersions(vResp.data.versions || []);
        setContentHash(vResp.data.content_hash || '');
      } catch {}
    } catch (error) {
      toast.error('Failed to load country');
      navigate('/platform/countries');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSection = async (sectionKey, userFeedback = '') => {
    try {
      setGeneratingSections(prev => ({ ...prev, [sectionKey]: true }));
      // Optional `user_feedback` is consumed by the backend, persisted to the
      // per-section chat thread, and merged into the LLM system prompt so
      // Claude only acts on items that are factually correct, on-topic, and
      // verifiable.
      const body = userFeedback ? { user_feedback: userFeedback } : {};
      await axios.post(`${API}/countries/${countryId}/generate-section/${sectionKey}?language=${lang || 'en'}`, body, { timeout: 15000 });
      
      let lastQueueMsg = null;
      const poll = async (attempts = 0) => {
        if (attempts > 90) {
          toast.error('Generation is taking too long');
          setGeneratingSections(prev => ({ ...prev, [sectionKey]: false }));
          return;
        }
        try {
          const statusResp = await axios.get(`${API}/countries/${countryId}/section-status/${sectionKey}`);
          if (statusResp.data.status === 'completed') {
            toast.dismiss(`queue-${sectionKey}`);
            toast.success(`${SECTIONS.find(s => s.key === sectionKey)?.title || sectionKey} generated!`);
            fetchCountry();
            setGeneratingSections(prev => ({ ...prev, [sectionKey]: false }));
            return;
          }
          if (statusResp.data.status === 'error') {
            toast.dismiss(`queue-${sectionKey}`);
            toast.error(`Failed: ${statusResp.data.error || 'Error'}`);
            setGeneratingSections(prev => ({ ...prev, [sectionKey]: false }));
            return;
          }
          // Surface queue position / generating state to the user
          const ahead = statusResp.data.queue_ahead || 0;
          const currentStatus = statusResp.data.status;
          const msg = currentStatus === 'queued'
            ? (ahead > 0 ? `Queued — ${ahead} section${ahead > 1 ? 's' : ''} ahead` : 'Queued — starting shortly…')
            : currentStatus === 'generating'
              ? 'Generating with our intelligence engine…'
              : null;
          if (msg && msg !== lastQueueMsg) {
            lastQueueMsg = msg;
            toast.loading(msg, { id: `queue-${sectionKey}`, duration: 60000 });
          }
        } catch {}
        setTimeout(() => poll(attempts + 1), 5000);
      };
      setTimeout(() => poll(0), 3000);
    } catch (error) {
      toast.error(getErrorMessage(error));
      setGeneratingSections(prev => ({ ...prev, [sectionKey]: false }));
    }
  };

  const handleGenerateIllustration = async (sectionKey) => {
    try {
      
      setGeneratingIllustrations(prev => ({ ...prev, [sectionKey]: true }));
      toast.loading('Generating AI illustration...', { id: `illust-${sectionKey}` });
      const response = await axios.post(`${API}/countries/${countryId}/generate-illustration/${sectionKey}`, {}, {
        timeout: 120000
      });
      // Accept any 200 response with an illustration_url — some branches (e.g. Claude HTML)
      // may not include response.data.success explicitly.
      if (response.data?.illustration_url || response.data?.success) {
        toast.dismiss(`illust-${sectionKey}`);
        toast.success('Illustration generated!');
        await fetchCountry();
      } else {
        toast.dismiss(`illust-${sectionKey}`);
        toast.warning('Illustration call returned unexpected payload — refreshing anyway');
        await fetchCountry();
      }
    } catch (error) {
      toast.dismiss(`illust-${sectionKey}`);
      toast.error(getErrorMessage(error));
    } finally {
      setGeneratingIllustrations(prev => ({ ...prev, [sectionKey]: false }));
    }
  };

  // Read the illustration URL for a section from EITHER storage location:
  // - legacy flat:   country["<key>_illustration"]
  // - new nested:    country.section_illustrations["<key>"]
  // Some generation paths (automation pipeline) historically wrote only to the
  // nested field, which is why illustrations past the first section sometimes
  // looked "missing" in the UI. The backend now writes to both locations for
  // every future illustration; this helper handles mixed-era data gracefully.
  const getSectionIllustration = (sectionKey) => {
    if (!country) return null;
    const flat = country[`${sectionKey}_illustration`];
    if (flat) return flat;
    const nested = country.section_illustrations?.[sectionKey];
    return nested || null;
  };

  const handleDownloadReportVersion = async (version) => {
    try {
      toast.loading(`Downloading report v${version}…`, { id: `rv-${version}` });
      const response = await axios.get(
        `${API}/countries/${countryId}/report-versions/${version}/download`,
        { responseType: 'blob', timeout: 120000 }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SA_${country?.name || 'Blueprint'}_v${version}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss(`rv-${version}`);
      toast.success(`Report v${version} downloaded`);
    } catch (error) {
      toast.dismiss(`rv-${version}`);
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Session expired — please log in again');
        localStorage.removeItem('token');
        setTimeout(() => { window.location.href = '/login'; }, 1200);
      } else if (status === 404) {
        toast.error(`Report v${version} no longer available in storage`);
      } else {
        toast.error('Download failed — please try again');
      }
    }
  };

  const handleDownloadPDF = async () => {
    const toastId = 'pdf-gen';
    // Two-step async flow — avoids proxy idle-read timeout on the long generation.
    //   1) POST /pdf/start → {job_id}
    //   2) Poll GET /pdf/status?job_id=X every 4s
    //   3) GET /pdf/download → stream from object storage (fast, no generation)
    // Falls back to the legacy sync endpoint if the async path errors out.
    const fallbackSync = async () => {
      toast.loading('Generating PDF report — this may take up to 90 seconds…', { id: toastId });
      const resp = await axios.get(
        `${API}/countries/${countryId}/blueprint/pdf?language=${lang || 'en'}`,
        { responseType: 'blob', timeout: 300000 },
      );
      triggerDownload(resp.data);
    };
    const triggerDownload = (blobData) => {
      const url = window.URL.createObjectURL(new Blob([blobData]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SA_${country?.name}_Blueprint_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss(toastId);
      toast.success('PDF downloaded!');
    };
    try {
      toast.loading('Starting PDF generation…', { id: toastId });
      let jobId;
      try {
        const startRes = await axios.post(
          `${API}/countries/${countryId}/blueprint/pdf/start?language=${lang || 'en'}`,
          {},
          { timeout: 20000 },
        );
        jobId = startRes.data?.job_id;
      } catch (_startErr) {
        // Start endpoint not available or failing — fall back to legacy sync
        await fallbackSync();
        try { const vr = await axios.get(`${API}/countries/${countryId}/report-versions`); setReportVersions(vr.data.versions || []); } catch {}
        return;
      }
      if (!jobId) throw new Error('No job_id returned');
      // Poll status every 4s for up to 12 minutes (image generation + LLM
      // tables + object-storage upload can legitimately take 5-8 min on the
      // production cluster for a 31-section blueprint).
      const maxPollMs = 12 * 60 * 1000;
      const pollStart = Date.now();
      let status = 'running';
      let lastProgress = 'Generating PDF — this may take up to 8 minutes…';
      toast.loading(lastProgress, { id: toastId });
      let consecutivePollErrs = 0;
      while (status === 'running') {
        await new Promise((r) => setTimeout(r, 4000));
        if (Date.now() - pollStart > maxPollMs) {
          throw new Error('PDF generation timed out after 12 minutes — please try again or use a cached version from the Versions list.');
        }
        try {
          const sRes = await axios.get(
            `${API}/countries/${countryId}/blueprint/pdf/status?job_id=${jobId}`,
            { timeout: 15000 },
          );
          status = sRes.data?.status || 'running';
          consecutivePollErrs = 0;
          if (sRes.data?.progress && sRes.data.progress !== lastProgress) {
            lastProgress = sRes.data.progress;
            toast.loading(`${lastProgress} (${Math.round((Date.now() - pollStart) / 1000)}s)`, { id: toastId });
          }
          if (status === 'failed') throw new Error(sRes.data?.error || 'PDF job failed');
        } catch (pollErr) {
          consecutivePollErrs += 1;
          if (consecutivePollErrs >= 6) {
            // 24+ seconds of continuous poll failures → escalate
            throw new Error('Lost connection to PDF job. Please retry.');
          }
          // eslint-disable-next-line no-console
          console.warn('PDF poll retry', pollErr?.message);
        }
      }
      // Download via object-storage streamer (fast — no generation)
      toast.loading('Downloading PDF…', { id: toastId });
      const dlRes = await axios.get(
        `${API}/countries/${countryId}/blueprint/pdf/download`,
        { responseType: 'blob', timeout: 120000 },
      );
      triggerDownload(dlRes.data);
      try { const vr = await axios.get(`${API}/countries/${countryId}/report-versions`); setReportVersions(vr.data.versions || []); } catch {}
    } catch (error) {
      toast.dismiss(toastId);
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Session expired — please log in again');
        localStorage.removeItem('token');
        setTimeout(() => { window.location.href = '/login'; }, 1200);
        return;
      }
      if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
        toast.error('PDF generation timed out — please try again.');
        return;
      }
      if (error.code === 'ERR_NETWORK' || /network error/i.test(error.message || '')) {
        toast.error('PDF stream interrupted. The file is large — please retry.');
        return;
      }
      const detail = error.response?.data?.detail;
      const safe = typeof detail === 'string' ? detail : (error.message || 'Please try again');
      toast.error(`PDF error: ${safe}`);
    }
  };

  const handleGenerateReferences = async () => {
    try {
      
      setGeneratingReferences(true);
      toast.loading('Compiling references...', { id: 'refs-gen' });
      const response = await axios.post(`${API}/countries/${countryId}/generate-references`, {}, {
        timeout: 60000
      });
      if (response.data.success) {
        toast.dismiss('refs-gen');
        toast.success('References compiled!');
        fetchCountry();
      }
    } catch (error) {
      toast.dismiss('refs-gen');
      toast.error(getErrorMessage(error));
    } finally { setGeneratingReferences(false); }
  };

  const handleGenerateAudio = async () => {
    try {
      
      setGeneratingAudio(true);
      toast.loading('Generating audio...', { id: 'audio-gen' });
      await axios.post(`${API}/countries/${countryId}/generate-audio-overview`, {}, {  });
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/countries/${countryId}/audio-overview/status`, {  });
          if (res.data.status === 'completed') { clearInterval(poll); toast.dismiss('audio-gen'); toast.success('Audio generated!'); setGeneratingAudio(false); fetchCountry(); }
          else if (res.data.status === 'failed') { clearInterval(poll); toast.dismiss('audio-gen'); toast.error('Audio failed'); setGeneratingAudio(false); }
        } catch {}
      }, 5000);
    } catch (error) { toast.dismiss('audio-gen'); toast.error(getErrorMessage(error)); setGeneratingAudio(false); }
  };

  const handleGenerateInfographic = async () => {
    try {
      
      setGeneratingInfographic(true);
      toast.loading('Generating infographic...', { id: 'infographic-gen' });
      await axios.post(`${API}/countries/${countryId}/generate-infographic`, {}, {  });
      const poll = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/countries/${countryId}/infographic/status`, {  });
          if (res.data.status === 'completed') { clearInterval(poll); toast.dismiss('infographic-gen'); toast.success('Infographic done!'); setGeneratingInfographic(false); fetchCountry(); }
          else if (res.data.status === 'failed') { clearInterval(poll); toast.dismiss('infographic-gen'); toast.error('Infographic generation failed. Please try again.'); setGeneratingInfographic(false); }
        } catch {}
      }, 5000);
    } catch (error) { toast.dismiss('infographic-gen'); toast.error(getErrorMessage(error)); setGeneratingInfographic(false); }
  };

  const handleRegenerateAllMissing = async () => {
    const sectionsDone = new Set(country?.sections_generated || []);
    const missing = SECTIONS.filter(s => !sectionsDone.has(s.key));
    if (missing.length === 0) { toast.info(`All ${SECTIONS.length} sections are already generated.`); return; }
    if (!window.confirm(`Queue ${missing.length} missing section${missing.length>1?'s':''}? They'll generate one-by-one (~${Math.ceil(missing.length * 1.3)} min).`)) return;
    toast.success(`Queued ${missing.length} sections — they'll run sequentially.`, { duration: 4000 });
    // Fire all POSTs quickly; backend lock serializes them. Each handleGenerateSection
    // manages its own polling and toasts.
    for (const s of missing) {
      handleGenerateSection(s.key);
      await new Promise(r => setTimeout(r, 300)); // small stagger so queue_ahead counts land right
    }
  };

  const handleRegenerateIncomplete = async () => {
    // Refetch latest completeness state before acting
    let incomplete = completeness?.incomplete || [];
    try {
      const r = await axios.get(`${API}/countries/${countryId}/sections-completeness`);
      setCompleteness(r.data);
      incomplete = r.data.incomplete || [];
    } catch { /* use cached */ }
    if (incomplete.length === 0) { toast.info('All generated sections are complete. ✓'); return; }
    if (!window.confirm(`${incomplete.length} section${incomplete.length>1?'s are':' is'} missing the End-of-Section marker (likely truncated). Regenerate now? They'll run sequentially (~${Math.ceil(incomplete.length * 1.3)} min).`)) return;
    toast.success(`Queued ${incomplete.length} incomplete section${incomplete.length>1?'s':''} — running sequentially.`, { duration: 4000 });
    for (const key of incomplete) {
      handleGenerateSection(key);
      await new Promise(r => setTimeout(r, 300));
    }
  };

  const handleGenerateAllIllustrations = async () => {
    // Only consider sections that have text content — cannot illustrate an empty section
    const generated = new Set(country?.sections_generated || []);
    const withText = SECTIONS.filter(s => generated.has(s.key));
    const missing = withText.filter(s => !getSectionIllustration(s.key));
    
    if (withText.length === 0) {
      toast.info('Generate text sections first — illustrations need section content to match.');
      return;
    }
    if (missing.length === 0) {
      toast.info('All illustrations are already generated.');
      return;
    }
    const eta = Math.ceil(missing.length * 0.4); // ~25s per illustration
    if (!window.confirm(
      `Generate ${missing.length} missing illustration${missing.length > 1 ? 's' : ''}? ` +
      `ETA ~${eta} min (sequential). Each illustration costs ~$0.04 in LLM credits. ` +
      `The provider (Gemini or Claude HTML) is whatever is selected on the User Management page.`
    )) return;
    
    toast.success(`Generating ${missing.length} illustrations sequentially…`, { duration: 4000 });
    
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < missing.length; i++) {
      const s = missing[i];
      toast.loading(
        `Illustration ${i + 1}/${missing.length}: ${s.title}`,
        { id: 'illust-batch', duration: 90000 }
      );
      try {
        setGeneratingIllustrations(prev => ({ ...prev, [s.key]: true }));
        await axios.post(
          `${API}/countries/${countryId}/generate-illustration/${s.key}`,
          {},
          { timeout: 120000 }
        );
        ok++;
      } catch (e) {
        failed++;
        // continue with remaining
      } finally {
        setGeneratingIllustrations(prev => ({ ...prev, [s.key]: false }));
      }
    }
    toast.dismiss('illust-batch');
    if (failed === 0) {
      toast.success(`Generated all ${ok} illustration${ok > 1 ? 's' : ''}.`);
    } else {
      toast.warning(`${ok} illustration${ok !== 1 ? 's' : ''} generated, ${failed} failed. Re-run to retry.`);
    }
    fetchCountry();
  };

  const openGammaModal = async () => {
    setShowGammaModal(true);
    if (gammaThemes.length === 0) {
      setLoadingThemes(true);
      try {
        const resp = await axios.get(`${API}/gamma/themes`);
        setGammaThemes(resp.data.themes || []);
      } catch { toast.error('Failed to load themes'); }
      finally { setLoadingThemes(false); }
    }
  };

  const handleGeneratePresentation = async () => {
    try {
      setShowGammaModal(false);
      setGeneratingPresentation(true);
      toast.loading('Generating Gamma presentation...', { id: 'gamma-gen' });
      const body = { ...gammaOptions };
      if (!body.themeId) delete body.themeId;
      if (!body.language) body.language = lang || 'en';
      const resp = await axios.post(`${API}/countries/${countryId}/generate-presentation`, body, { timeout: 60000 });
      
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const statusResp = await axios.get(`${API}/countries/${countryId}/presentation/status`);
          if (statusResp.data.status === 'completed' && statusResp.data.url) {
            toast.dismiss('gamma-gen');
            toast.success('Presentation ready!');
            setPresentationUrl(statusResp.data.url);
            setGeneratingPresentation(false);
            // Refresh versions
            try { const vr = await axios.get(`${API}/countries/${countryId}/report-versions`); setReportVersions(vr.data.versions || []); } catch {}
            return;
          }
          if (statusResp.data.status === 'failed') {
            toast.dismiss('gamma-gen');
            toast.error('Presentation generation failed');
            setGeneratingPresentation(false);
            return;
          }
        } catch {}
        if (attempts < 30) {
          setTimeout(poll, 5000);
        } else {
          toast.dismiss('gamma-gen');
          toast.error('Presentation is taking too long — check back later');
          setGeneratingPresentation(false);
        }
      };
      setTimeout(poll, 8000);
    } catch (error) {
      toast.dismiss('gamma-gen');
      toast.error(`Presentation error: ${error.response?.data?.detail || 'Failed'}`);
      setGeneratingPresentation(false);
    }
  };

  const scrollToSection = (key) => {
    setActiveSection(key);
    sectionRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const sectionsGenerated = country?.sections_generated || [];
  const progress = sectionsGenerated.length;

  const renderContent = (sectionKey) => {
    const content = country?.[sectionKey];
    if (!content) return (
      <div className="text-center py-16 text-[#737686]">
        <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="italic">Section not generated yet</p>
      </div>
    );
    // Strip the leading VERDICT: line — it's rendered as a banner above the content
    const stripVerdict = (t) => typeof t === 'string'
      ? t.replace(/^\s*VERDICT:\s*[^\n]*\n+/i, '')
      : t;
    if (sectionKey === 'pestle_analysis' && typeof content === 'object') {
      return (
        <div className="space-y-8">
          {Object.entries(content).map(([key, value]) => (
            <div key={key}>
              <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#004ac6] mb-3">{key}</h3>
              <div className="section-content prose prose-slate max-w-none prose-p:text-[#434655] prose-p:leading-relaxed prose-strong:text-[#0b1c30] prose-table:border prose-td:border prose-td:px-3 prose-td:py-1.5 prose-th:border prose-th:px-3 prose-th:py-1.5 prose-th:bg-[#eff4ff]">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{preprocessMarkdown(typeof value === 'string' ? value : JSON.stringify(value))}</ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (typeof content === 'string') {
      return (
        <div className="section-content prose prose-slate max-w-none prose-headings:text-[#0b1c30] prose-headings:tracking-tight prose-strong:text-[#0b1c30] prose-p:text-[#434655] prose-p:leading-relaxed prose-table:border prose-td:border prose-td:px-3 prose-td:py-1.5 prose-th:border prose-th:px-3 prose-th:py-1.5 prose-th:bg-[#eff4ff] prose-li:text-[#434655]">
          {sectionKey === 'stakeholder_map' && (
            <StakeholderPowerInterestGrid markdown={content} />
          )}
          {sectionKey === 'portfolio_benchmark' && (
            <PortfolioBenchmarkHeatmap markdown={content} />
          )}
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{preprocessMarkdown(stripVerdict(content))}</ReactMarkdown>
        </div>
      );
    }
    if (typeof content === 'object') {
      return (
        <div className="space-y-6">
          {Object.entries(content).map(([key, value]) => (
            <div key={key}>
              <h4 className="font-semibold text-[#0b1c30] mb-2">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
              <div className="section-content prose prose-slate max-w-none prose-p:text-[#434655] prose-p:leading-relaxed">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{preprocessMarkdown(typeof value === 'string' ? value : JSON.stringify(value))}</ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
      );
    }
    return <p className="text-[#434655]">{String(content)}</p>;
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#f8f9ff]"><Loader2 className="w-8 h-8 animate-spin text-[#004ac6]" /></div>;
  if (!country) return null;

  return (
    <div data-testid="blueprint-view-page" className="bg-[#f8f9ff] min-h-screen text-[#0b1c30]" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Dynamic Hero Banner — kinetic grid + slow-spin radial */}
      <style>{`
        @keyframes slowSpinBv { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-slowSpinBv { animation: slowSpinBv 45s linear infinite; }
        .grid-bg-bv { background-image: linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px); background-size: 48px 48px; }
        @keyframes shimmerBv { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .shimmer-bv { background: linear-gradient(90deg, transparent, rgba(167,139,250,0.12), transparent); background-size: 200% 100%; animation: shimmerBv 8s linear infinite; }
      `}</style>
      <div className="relative w-full h-[340px] bg-gradient-to-br from-[#0b1c30] via-[#1a365d] to-[#004ac6] overflow-hidden">
        <div className="absolute inset-0 grid-bg-bv opacity-70 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full animate-slowSpinBv pointer-events-none"
             style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.22) 0%, rgba(37,99,235,0.12) 40%, transparent 70%)' }} />
        <div className="absolute inset-0 shimmer-bv pointer-events-none" />
        <div className="absolute top-6 left-6 z-10">
          <Button onClick={() => navigate('/platform/countries')} variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10 gap-2" size="sm">
            <ArrowLeft className="w-4 h-4" /> Back to Countries
          </Button>
        </div>
        <div className="absolute bottom-8 left-8 right-8 z-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 bg-[#004ac6]/90 text-white text-[10px] font-black px-2.5 py-1 rounded tracking-widest uppercase backdrop-blur-sm border border-white/10">
              <span className="w-1.5 h-1.5 rounded-full bg-[#a78bfa] animate-pulse" />
              Intelligence Report
            </span>
            <span className="text-white/50 text-xs font-medium">Updated {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-5 leading-[1.05]">
            <span className="text-white">{country.name}</span>
            <span className="bg-gradient-to-r from-[#a78bfa] to-[#60a5fa] bg-clip-text text-transparent"> · Blueprint</span>
          </h1>
          <div className="flex items-center gap-6 text-sm flex-wrap">
            <div className="flex flex-col">
              <span className="text-white/40 text-[10px] uppercase tracking-wider">Sector</span>
              <span className="text-white font-semibold">{country.sector || 'General'}</span>
            </div>
            <div className="flex flex-col border-l border-white/20 pl-6">
              <span className="text-white/40 text-[10px] uppercase tracking-wider">Region</span>
              <span className="text-white font-semibold">{country.region || 'Unclassified'}</span>
            </div>
            <div className="flex flex-col border-l border-white/20 pl-6">
              <span className="text-white/40 text-[10px] uppercase tracking-wider">Sections</span>
              <span className="text-white font-semibold">{progress}/{SECTIONS.length} Generated</span>
            </div>
            <div className="flex flex-col border-l border-white/20 pl-6">
              <span className="text-white/40 text-[10px] uppercase tracking-wider">Analyst</span>
              <span className="text-white font-semibold">Sovereign IQ</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Journey Progress Strip */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#c3c6d7]/30 shadow-sm" data-testid="journey-progress-strip">
        <style>{`
          @keyframes progressShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
          .progress-shimmer-bar::after { content:''; position:absolute; inset:0; background:linear-gradient(90deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%); background-size: 200% 100%; animation: progressShimmer 2.2s linear infinite; border-radius: inherit; }
          @keyframes fillGrow { from { width: 0%; } to { width: var(--target); } }
          .progress-fill-anim { animation: fillGrow 900ms cubic-bezier(0.22, 1, 0.36, 1) both; }
        `}</style>
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-4 flex-wrap">
          {/* Overall percent */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="relative w-11 h-11 shrink-0">
              <svg viewBox="0 0 44 44" className="w-11 h-11 -rotate-90">
                <circle cx="22" cy="22" r="18" stroke="#e5eeff" strokeWidth="4" fill="none" />
                <circle
                  cx="22" cy="22" r="18"
                  stroke="url(#journeyGrad)" strokeWidth="4" fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${(progress / SECTIONS.length) * (2 * Math.PI * 18)} ${2 * Math.PI * 18}`}
                  style={{ transition: 'stroke-dasharray 800ms cubic-bezier(0.22,1,0.36,1)' }}
                />
                <defs>
                  <linearGradient id="journeyGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#004ac6" />
                    <stop offset="100%" stopColor="#7c3aed" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-[10px] font-extrabold text-[#0b1c30]">
                {Math.round((progress / SECTIONS.length) * 100)}%
              </div>
            </div>
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-[#737686]">Journey</div>
              <div className="text-sm font-bold text-[#0b1c30]">{progress} <span className="text-[#737686] font-medium">of {SECTIONS.length} sections</span></div>
            </div>
          </div>

          {/* Per-tier fill bars */}
          <div className="flex items-center gap-4 flex-1 min-w-[320px] flex-wrap">
            {(() => {
              const tiers = [
                { key: 'scout',      label: 'Scout',      from: '#004ac6', to: '#2563eb', total: SECTIONS.filter(s => s.tier === 'scout').length },
                { key: 'strategist', label: 'Strategist', from: '#00A651', to: '#059669', total: SECTIONS.filter(s => s.tier === 'strategist').length },
                { key: 'sovereign',  label: 'Sovereign',  from: '#7c3aed', to: '#db2777', total: SECTIONS.filter(s => s.tier === 'sovereign').length },
              ];
              return tiers.map(t => {
                const done = SECTIONS.filter(s => s.tier === t.key && sectionsGenerated.includes(s.key)).length;
                const pct = t.total ? (done / t.total) * 100 : 0;
                const inFlight = SECTIONS.some(s => s.tier === t.key && generatingSections[s.key]);
                return (
                  <div key={t.key} className="flex-1 min-w-[140px]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: t.from }}>
                        {t.label}
                        {inFlight && <span className="ml-1.5 inline-block w-1 h-1 rounded-full bg-current animate-ping" />}
                      </span>
                      <span className="text-[10px] font-mono font-bold text-[#0b1c30]">{done}/{t.total}</span>
                    </div>
                    <div className="relative h-1.5 rounded-full bg-[#eff4ff] overflow-hidden">
                      <div
                        className={`absolute inset-y-0 left-0 rounded-full progress-fill-anim ${inFlight ? 'progress-shimmer-bar' : ''}`}
                        style={{
                          '--target': `${pct}%`,
                          width: `${pct}%`,
                          background: `linear-gradient(90deg, ${t.from}, ${t.to})`,
                          transition: 'width 800ms cubic-bezier(0.22,1,0.36,1)',
                        }}
                      />
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex max-w-[1600px] mx-auto px-6 gap-8 mt-8">
        {/* Sticky Sidebar */}
        <aside className="hidden lg:flex flex-col w-72 shrink-0 sticky top-8 self-start space-y-6 overflow-y-auto pr-1 scrollbar-thin" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
          {/* Report Index */}
          <div className="p-5 bg-white rounded-xl shadow-sm">
            <div className="flex items-center gap-2 mb-5">
              <FileText className="w-4 h-4 text-[#004ac6]" />
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-[#434655]">Report Index</span>
            </div>
            <nav className="space-y-1">
              {['scout', 'strategist', 'sovereign'].map(tier => {
                const tierSections = SECTIONS.filter(s => s.tier === tier);
                const info = TIER_INFO[tier];
                return (
                  <div key={tier}>
                    <div className="flex items-center gap-1.5 px-2 py-1.5 mt-1 first:mt-0">
                      <span className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: info.color, backgroundColor: info.bg }}>{info.name}</span>
                    </div>
                    {tierSections.map((section) => {
                      const Icon = section.icon;
                      const isActive = activeSection === section.key;
                      const isGenerated = sectionsGenerated.includes(section.key);
                      return (
                        <button key={section.key} onClick={() => scrollToSection(section.key)}
                          data-testid={`nav-${section.key}`}
                          className={`w-full flex items-center justify-between group p-2 rounded-lg text-xs transition-all ${
                            isActive ? 'bg-[#e5eeff] text-[#004ac6] font-semibold' : 'text-[#434655] hover:text-[#004ac6] hover:bg-[#eff4ff]'
                          }`}>
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] font-bold w-4 text-center" style={{ color: info.color }}>{section.num}</span>
                            <span className="truncate">{section.shortTitle}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {isGenerated && <div className="w-1.5 h-1.5 rounded-full bg-green-500" />}
                            {isActive && <ChevronRight className="w-3 h-3" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              {/* Trends nav item */}
              <button onClick={() => { setActiveSection('trends'); document.getElementById('country-trends-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                data-testid="nav-trends"
                className={`w-full flex items-center justify-between group p-2.5 rounded-lg text-sm transition-all ${
                  activeSection === 'trends' ? 'bg-[#e5eeff] text-[#004ac6] font-semibold' : 'text-[#434655] hover:text-[#004ac6] hover:bg-[#eff4ff]'
                }`}>
                <div className="flex items-center gap-2.5">
                  <TrendingUp className="w-4 h-4" />
                  <span className="truncate">Economic Trends</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#004ac6]" />
                  {activeSection === 'trends' && <ChevronRight className="w-3.5 h-3.5" />}
                </div>
              </button>
            </nav>
          </div>

          {/* Quick Export */}
          <div className="p-5 bg-[#2563eb] text-white rounded-xl flex flex-col gap-3">
            <span className="text-[10px] font-bold uppercase tracking-widest opacity-80">Quick Export</span>
            <p className="text-sm opacity-90">Download the full intelligence report as a branded PDF.</p>
            <Button onClick={handleDownloadPDF} className="bg-white text-[#004ac6] hover:bg-white/90 text-xs font-bold gap-2 mt-1" size="sm" data-testid="sidebar-pdf-btn">
              <Download className="w-3.5 h-3.5" /> PDF Report
            </Button>
            <Button onClick={openGammaModal} disabled={generatingPresentation}
              className="bg-white/15 text-white hover:bg-white/25 border border-white/30 text-xs font-bold gap-2" size="sm" data-testid="sidebar-gamma-btn">
              {generatingPresentation ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Presentation className="w-3.5 h-3.5" />}
              {generatingPresentation ? 'Creating Deck...' : presentationUrl ? 'Regenerate Presentation' : 'Generate Presentation'}
            </Button>
            {presentationUrl && (
              <div className="flex items-center gap-3 mt-1">
                <a href={presentationUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs font-medium text-white/90 hover:text-white" data-testid="gamma-link">
                  <Presentation className="w-3 h-3" /> Open in Gamma
                </a>
                <span className="text-[10px] text-white/50">Export PDF from Gamma menu</span>
              </div>
            )}
          </div>

          {/* Report Versions */}
          {reportVersions.length > 0 && (
            <div className="p-5 bg-white rounded-xl shadow-sm" data-testid="report-versions-panel">
              <span className="text-xs font-bold uppercase tracking-[0.1em] text-[#434655] mb-3 block">Report Versions</span>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {reportVersions.map((v, i) => (
                  <div key={`${v.type}-${v.version}-${i}`} className="flex items-center justify-between p-2.5 rounded-lg bg-[#f8f9ff] border border-[#e5eeff]/50 hover:border-[#004ac6]/30 transition-all group">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${v.type === 'pdf' ? 'bg-red-50 text-red-500' : 'bg-purple-50 text-purple-500'}`}>
                        {v.type === 'pdf' ? <FileText className="w-3.5 h-3.5" /> : <Presentation className="w-3.5 h-3.5" />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-[#0b1c30] truncate">
                          {v.type === 'pdf' ? `PDF Report` : `Presentation`} <span className="text-[#004ac6]">v{v.version}</span>
                        </div>
                        <div className="text-[10px] text-[#737686]">
                          {new Date(v.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          {v.language && v.language !== 'en' ? ` (${v.language.toUpperCase()})` : ''}
                        </div>
                      </div>
                    </div>
                    {v.type === 'pdf' ? (
                      <button onClick={() => handleDownloadReportVersion(v.version)}
                        className="p-1.5 rounded-md hover:bg-[#e5eeff] text-[#737686] hover:text-[#004ac6] transition-all opacity-60 group-hover:opacity-100"
                        title="Download PDF" data-testid={`download-pdf-v${v.version}`}>
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <a href={v.url} target="_blank" rel="noopener noreferrer"
                        className="p-1.5 rounded-md hover:bg-purple-50 text-[#737686] hover:text-purple-600 transition-all opacity-60 group-hover:opacity-100"
                        title="Open in Gamma (export PDF from menu)" data-testid={`open-gamma-v${v.version}`}>
                        <Presentation className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Studio Quick Access */}
          <div className="p-5 bg-white rounded-xl shadow-sm">
            <span className="text-xs font-bold uppercase tracking-[0.1em] text-[#434655] mb-3 block">AI Studio</span>

            {/* Strict 4-stage sequential pipeline (sections → images → infographic → references). */}
            <div className="mb-4">
              <FullBlueprintPipelinePanel countryId={countryId} onComplete={fetchCountry} />
            </div>

            {/* Completeness badge */}
            {completeness && completeness.total_generated > 0 && (
              <div className={`mb-3 p-2.5 rounded-lg border text-[11px] ${completeness.incomplete_count === 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`} data-testid="completeness-badge">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="font-bold">
                    {completeness.incomplete_count === 0
                      ? `✓ All ${completeness.complete_count} section${completeness.complete_count > 1 ? 's are' : ' is'} complete`
                      : `⚠ ${completeness.incomplete_count} incomplete · ${completeness.complete_count} complete`}
                  </span>
                </div>
                {completeness.incomplete_count > 0 && (
                  <div className="text-[10px] opacity-80 leading-tight">Missing the &quot;End of Section&quot; marker — likely truncated.</div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Button onClick={handleRegenerateAllMissing} disabled={Object.values(generatingSections).some(Boolean)} variant="default" size="sm" className="w-full justify-start gap-2 text-xs bg-[#004ac6] hover:bg-[#003a9c]" data-testid="sidebar-regen-all-btn">
                <RefreshCw className="w-3.5 h-3.5" /> Regenerate All Missing
              </Button>
              {completeness?.incomplete_count > 0 && (
                <Button onClick={handleRegenerateIncomplete} disabled={Object.values(generatingSections).some(Boolean)} variant="outline" size="sm" className="w-full justify-start gap-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="sidebar-regen-incomplete-btn">
                  <RefreshCw className="w-3.5 h-3.5" /> Regenerate Incomplete ({completeness.incomplete_count})
                </Button>
              )}
              {(() => {
                const generated = new Set(country?.sections_generated || []);
                const withText = SECTIONS.filter(s => generated.has(s.key));
                const missingCount = withText.filter(s => !getSectionIllustration(s.key)).length;
                const genInFlight = Object.values(generatingIllustrations).some(Boolean);
                return (
                  <Button
                    onClick={handleGenerateAllIllustrations}
                    disabled={genInFlight || missingCount === 0}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start gap-2 text-xs border-[#00A651]/30 text-[#00A651] hover:bg-[#ecfdf5] disabled:opacity-60"
                    data-testid="sidebar-illustrate-all-btn"
                    title={missingCount === 0 ? 'All illustrations already generated' : `${missingCount} section${missingCount > 1 ? 's' : ''} missing illustration`}
                  >
                    {genInFlight ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                    <span className="flex-1 text-left">Generate All Missing Illustrations</span>
                    {missingCount > 0 && (
                      <span className="ml-auto px-1.5 py-0.5 rounded-full bg-[#00A651]/10 text-[10px] font-bold">{missingCount}</span>
                    )}
                  </Button>
                );
              })()}
              <Button onClick={handleGenerateAudio} disabled={generatingAudio} variant="outline" size="sm" className="w-full justify-start gap-2 text-xs" data-testid="sidebar-audio-btn">
                <Headphones className="w-3.5 h-3.5" /> {generatingAudio ? 'Generating...' : country.audio_overview_path ? 'Regenerate Audio' : 'Generate Audio'}
              </Button>
              <Button onClick={handleGenerateInfographic} disabled={generatingInfographic} variant="outline" size="sm" className="w-full justify-start gap-2 text-xs" data-testid="sidebar-infographic-btn">
                <BarChart3 className="w-3.5 h-3.5" /> {generatingInfographic ? 'Generating...' : country.infographic_url ? 'Regenerate Infographic' : 'Generate Infographic'}
              </Button>
              <Button onClick={handleGenerateReferences} disabled={generatingReferences} variant="outline" size="sm" className="w-full justify-start gap-2 text-xs" data-testid="sidebar-refs-btn">
                <BookOpen className="w-3.5 h-3.5" /> {generatingReferences ? 'Compiling...' : country.blueprint_references ? 'Refresh References' : 'Generate References'}
              </Button>
            </div>
            {country.audio_overview_path && (
              <div className="mt-3">
                <audio src={`${API}/countries/${countryId}/audio-overview`} controls className="w-full h-8" data-testid="audio-player" />
              </div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 pb-24 min-w-0">
          {/* Key Metrics Bar */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12" data-testid="metrics-bar">
            <div className="bg-white p-6 rounded-xl shadow-sm border-t-4 border-[#004ac6]">
              <span className="text-[#434655] text-[10px] font-bold uppercase tracking-widest">Blueprint Progress</span>
              <div className="mt-3 flex items-end gap-2">
                <span className="text-4xl font-bold tracking-tighter">{progress}</span>
                <span className="text-sm text-[#434655] font-medium mb-1">/ {SECTIONS.length} sections</span>
              </div>
              <div className="w-full bg-[#e5eeff] h-1.5 rounded-full mt-3 overflow-hidden">
                <div className="bg-[#004ac6] h-full rounded-full transition-all" style={{ width: `${(progress / SECTIONS.length) * 100}%` }} />
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <span className="text-[#434655] text-[10px] font-bold uppercase tracking-widest">Sector Focus</span>
              <div className="mt-3">
                <span className="text-lg font-bold tracking-tight">{country.sector || 'General'}</span>
              </div>
              <p className="text-xs text-[#434655] mt-2">AI-tailored analysis for this sector</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <span className="text-[#434655] text-[10px] font-bold uppercase tracking-widest">AI Assets</span>
              <div className="mt-3 flex gap-3">
                {[
                  { label: 'Audio', has: !!country.audio_overview_path },
                  { label: 'Infographic', has: !!country.infographic_url },
                  { label: 'References', has: !!country.blueprint_references },
                ].map(a => (
                  <div key={a.label} className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full ${a.has ? 'bg-green-500' : 'bg-[#c3c6d7]'}`} />
                    <span className="text-[9px] text-[#434655] mt-1">{a.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Country Economic Trends */}
          <section className="mb-12" data-testid="country-trends-section">
            <h2 className="text-xl font-bold text-[#0b1c30] tracking-tight flex items-center gap-3 mb-5">
              <span className="w-8 h-8 rounded-lg bg-[#e5eeff] flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-[#004ac6]" />
              </span>
              Economic Trends — World Bank Data
            </h2>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <CountryTrendView countryId={countryId} countryName={country.name} />
            </div>
          </section>

          {/* Sections */}
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const isGenerated = sectionsGenerated.includes(section.key);
            const isGenerating = generatingSections[section.key];
            const isDark = section.key === 'financial_projections';
            const sectionDate = country?.section_dates?.[section.key];
            const regenCount = country?.section_regen_counts?.[section.key] || 0;

            return (
              <section key={section.key} ref={el => sectionRefs.current[section.key] = el}
                id={section.key} className="mb-12 scroll-mt-8" data-testid={`section-${section.key}`}>
                {/* Section Header */}
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl font-bold text-[#0b1c30] tracking-tight flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-[#e5eeff] flex items-center justify-center">
                      <Icon className="w-4 h-4 text-[#004ac6]" />
                    </span>
                    {section.title}
                    {isGenerated && <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full uppercase tracking-wider">Generated</span>}
                    {isGenerated && sectionDate && (
                      <span className="flex items-center gap-1 text-[10px] font-medium text-[#737686] bg-[#f0f1f5] px-2 py-0.5 rounded-full" data-testid={`date-${section.key}`}>
                        <Calendar className="w-3 h-3" />
                        {new Date(sectionDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    )}
                    {isGenerated && (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${regenCount >= 10 ? 'text-red-600 bg-red-50' : 'text-[#737686] bg-[#f0f1f5]'}`} data-testid={`regen-${section.key}`}>
                        {regenCount}/10 regenerations
                      </span>
                    )}
                  </h2>
                  <div className="flex gap-2">
                    {isGenerated && (
                      <Button onClick={() => handleGenerateIllustration(section.key)}
                        disabled={generatingIllustrations[section.key]}
                        variant="outline" size="sm" className="gap-1.5 text-xs">
                        {generatingIllustrations[section.key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
                        Illustration
                      </Button>
                    )}
                    <Button onClick={() => handleGenerateSection(section.key)}
                      disabled={isGenerating}
                      size="sm" className={`gap-1.5 text-xs ${isGenerated ? 'bg-[#434655] hover:bg-[#0b1c30]' : 'bg-[#004ac6] hover:bg-[#003ea8]'} text-white`}>
                      {isGenerating ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</> :
                        <>{isGenerated ? <RefreshCw className="w-3 h-3" /> : <FileText className="w-3 h-3" />} {isGenerated ? 'Regenerate' : 'Generate'}</>}
                    </Button>
                  </div>
                </div>

                {/* Quality metrics strip — Verdict (exec summary only) + Density + Freshness */}
                {isGenerated && (
                  <SectionQualityPanel countryId={countryId} sectionKey={section.key} darkMode={isDark} />
                )}

                {/* Notes & AI Chat — leave revision notes for the next regen,
                    or ask follow-up questions about the section without
                    modifying it. Persists per-section in MongoDB. */}
                <SectionChatBox
                  countryId={countryId}
                  sectionKey={section.key}
                  sectionTitle={section.title}
                  isGenerating={!!isGenerating}
                  isSectionGenerated={!!isGenerated}
                  onRegenerate={(notes) => handleGenerateSection(section.key, notes)}
                  darkMode={isDark}
                />

                {/* Section Content */}
                {isDark && isGenerated ? (
                  <div className="bg-[#0b1c30] text-white p-8 rounded-xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#004ac6] opacity-20 blur-[100px] -mr-32 -mt-32" />
                    <div className="relative z-10">
                      {renderContent(section.key)}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-8 rounded-xl shadow-sm">
                    {renderContent(section.key)}
                  </div>
                )}

                {/* Illustration — reads from both nested (section_illustrations.<key>) and flat (<key>_illustration) */}
                {isGenerated && getSectionIllustration(section.key) && (
                  <div className="mt-4 bg-white p-3 rounded-xl shadow-sm border border-[#c3c6d7]/20 overflow-hidden">
                    <img src={getSectionIllustration(section.key).startsWith('data:')
                      ? getSectionIllustration(section.key)
                      : `${API}/countries/${countryId}/illustration/${section.key}`}
                      alt={`${section.title} Illustration`} className="w-full h-auto rounded-lg"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                    <p className="text-[10px] text-[#434655] uppercase tracking-widest text-center font-semibold mt-2 pb-1">
                      AI-generated illustration — Sovereign IQ Intelligence
                    </p>
                  </div>
                )}

                {/* Sources used — lazy-loaded curated pool injected into this section's prompt */}
                {isGenerated && (
                  <SectionSourcesPanel countryId={countryId} sectionKey={section.key} darkMode={isDark} />
                )}
                {/* Live data signals — fresh API numbers (≤45d) + deep anchors, same block injected into prompt */}
                {isGenerated && (
                  <SectionLiveDataPanel countryId={countryId} sectionKey={section.key} darkMode={isDark} />
                )}
              </section>
            );
          })}

          {/* Infographic */}
          {country.infographic_url && (
            <section className="mb-12">
              <h2 className="text-xl font-bold text-[#0b1c30] tracking-tight flex items-center gap-3 mb-5">
                <span className="w-8 h-8 rounded-lg bg-[#e5eeff] flex items-center justify-center">
                  <BarChart3 className="w-4 h-4 text-[#004ac6]" />
                </span>
                Summary Infographic
              </h2>
              <div className="bg-white p-3 rounded-xl shadow-sm">
                <img src={`${API}/countries/${countryId}/infographic`} alt="Blueprint Infographic" className="w-full rounded-lg cursor-pointer"
                  onClick={() => window.open(`${API}/countries/${countryId}/infographic`, '_blank')} />
              </div>
            </section>
          )}

          {/* Video — disabled for V2 */}

          {/* Auto-generated Bibliography — audits every inline citation */}
          <section className="mb-12" data-testid="auto-references-section">
            <BlueprintReferencesPanel countryId={countryId} countryName={country.name} />
          </section>

          {/* Legacy narrative references (if populated by older flow) */}
          {country.blueprint_references && (
            <section className="mb-12" data-testid="references-section">
              <h2 className="text-xl font-bold text-[#0b1c30] tracking-tight flex items-center gap-3 mb-5">
                <span className="w-8 h-8 rounded-lg bg-[#e5eeff] flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-[#004ac6]" />
                </span>
                Sources & References
              </h2>
              <div className="bg-white p-8 rounded-xl shadow-sm">
                <div className="section-content prose prose-slate max-w-none prose-headings:text-[#0b1c30] prose-p:text-[#434655] prose-li:text-[#434655] prose-a:text-[#004ac6] prose-a:underline text-sm">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{country.blueprint_references}</ReactMarkdown>
                </div>
              </div>
            </section>
          )}

          {/* Footer */}
          <footer className="mt-16 pt-8 border-t border-[#c3c6d7]/20 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo-big.png" alt="Sovereign IQ Intelligence Platform" className="h-10 w-auto object-contain" />
            </div>
            <div className="text-[10px] text-[#434655] opacity-60">
              Confidential — For Investment Use Only — Sovereign IQ Intelligence
            </div>
          </footer>
        </main>
      </div>

      {/* Gamma Presentation Options Modal */}
      {showGammaModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowGammaModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="gamma-modal">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-[#e5e7eb]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#004ac6]/10 flex items-center justify-center">
                  <Presentation className="w-5 h-5 text-[#004ac6]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#0b1c30]">Presentation Options</h2>
                  <p className="text-xs text-[#737686]">Configure your Gamma presentation before generating</p>
                </div>
              </div>
              <button onClick={() => setShowGammaModal(false)} className="p-2 hover:bg-[#f3f4f6] rounded-lg transition-colors">
                <X className="w-4 h-4 text-[#737686]" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto max-h-[calc(85vh-140px)] space-y-6">
              {/* Theme Selection */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Palette className="w-4 h-4 text-[#004ac6]" />
                  <span className="text-sm font-bold text-[#0b1c30]">Theme Style</span>
                </div>
                {loadingThemes ? (
                  <div className="flex items-center gap-2 py-4 justify-center text-[#737686]">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading themes...
                  </div>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-48 overflow-y-auto pr-1">
                    <button onClick={() => setGammaOptions(p => ({...p, themeId: ''}))}
                      className={`p-2.5 rounded-lg border text-center text-xs transition-all ${!gammaOptions.themeId ? 'border-[#004ac6] bg-[#e5eeff] text-[#004ac6] font-semibold' : 'border-[#e5e7eb] hover:border-[#004ac6]/40 text-[#434655]'}`}
                      data-testid="theme-auto">
                      Auto
                    </button>
                    {gammaThemes.map(t => (
                      <button key={t.id} onClick={() => setGammaOptions(p => ({...p, themeId: t.id}))}
                        className={`p-2.5 rounded-lg border text-center text-xs transition-all truncate ${gammaOptions.themeId === t.id ? 'border-[#004ac6] bg-[#e5eeff] text-[#004ac6] font-semibold' : 'border-[#e5e7eb] hover:border-[#004ac6]/40 text-[#434655]'}`}
                        title={`${t.name} — ${t.tone?.join(', ')}`}
                        data-testid={`theme-${t.id}`}>
                        {t.name}
                      </button>
                    ))}
                  </div>
                )}
                {gammaOptions.themeId && (
                  <p className="text-[10px] text-[#737686] mt-1.5">
                    Selected: <span className="font-semibold text-[#004ac6]">{gammaThemes.find(t => t.id === gammaOptions.themeId)?.name}</span>
                    {' — '}{gammaThemes.find(t => t.id === gammaOptions.themeId)?.tone?.slice(0,3).join(', ')}
                  </p>
                )}
              </div>

              {/* Slide Count */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <SlidersHorizontal className="w-4 h-4 text-[#004ac6]" />
                  <span className="text-sm font-bold text-[#0b1c30]">Number of Slides</span>
                  <span className="text-xs font-bold text-[#004ac6] bg-[#e5eeff] px-2 py-0.5 rounded-full ml-auto">{gammaOptions.numCards}</span>
                </div>
                <input type="range" min={5} max={25} step={1} value={gammaOptions.numCards}
                  onChange={e => setGammaOptions(p => ({...p, numCards: parseInt(e.target.value)}))}
                  className="w-full accent-[#004ac6] h-2" data-testid="gamma-slides-slider" />
                <div className="flex justify-between text-[10px] text-[#737686] mt-1">
                  <span>5 (Brief)</span><span>14 (Standard)</span><span>25 (Comprehensive)</span>
                </div>
              </div>

              {/* Text Density */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Type className="w-4 h-4 text-[#004ac6]" />
                  <span className="text-sm font-bold text-[#0b1c30]">Text Density</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: 'brief', label: 'Brief', desc: 'Key points only' },
                    { value: 'generate', label: 'Standard', desc: 'Balanced content' },
                    { value: 'detailed', label: 'Detailed', desc: 'In-depth analysis' },
                    { value: 'extensive', label: 'Extensive', desc: 'Full report text' },
                  ].map(opt => (
                    <button key={opt.value} onClick={() => setGammaOptions(p => ({...p, textMode: opt.value}))}
                      className={`p-3 rounded-lg border text-center transition-all ${gammaOptions.textMode === opt.value ? 'border-[#004ac6] bg-[#e5eeff]' : 'border-[#e5e7eb] hover:border-[#004ac6]/40'}`}
                      data-testid={`textmode-${opt.value}`}>
                      <div className={`text-xs font-bold ${gammaOptions.textMode === opt.value ? 'text-[#004ac6]' : 'text-[#0b1c30]'}`}>{opt.label}</div>
                      <div className="text-[10px] text-[#737686] mt-0.5">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Language */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Languages className="w-4 h-4 text-[#004ac6]" />
                  <span className="text-sm font-bold text-[#0b1c30]">Presentation Language</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'en', label: 'English' },
                    { value: 'fr', label: 'Fran\u00e7ais' },
                    { value: 'pt', label: 'Portugu\u00eas' },
                  ].map(opt => (
                    <button key={opt.value} onClick={() => setGammaOptions(p => ({...p, language: opt.value}))}
                      className={`p-2.5 rounded-lg border text-center text-xs font-medium transition-all ${(gammaOptions.language || lang) === opt.value ? 'border-[#004ac6] bg-[#e5eeff] text-[#004ac6]' : 'border-[#e5e7eb] hover:border-[#004ac6]/40 text-[#434655]'}`}
                      data-testid={`gamma-lang-${opt.value}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-[#e5e7eb] flex items-center justify-between bg-[#f9fafb]">
              <p className="text-[10px] text-[#737686]">Powered by Gamma AI — uses credits from your account</p>
              <div className="flex items-center gap-2">
                <Button onClick={() => setShowGammaModal(false)} variant="outline" size="sm" className="text-xs">Cancel</Button>
                <Button onClick={handleGeneratePresentation} className="bg-[#004ac6] text-white text-xs font-bold gap-2" size="sm" data-testid="gamma-generate-btn">
                  <Presentation className="w-3.5 h-3.5" /> Generate Presentation
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlueprintViewPage;
